// src/modulos/Parametros/QualityCriteriaPage.js
import React, { useState, useMemo, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

// Constantes
const QUALITY_CRITERIA_COLLECTION = 'parameters/data/criterio-qualidade';
const ITEMS_PER_PAGE = 10;

const ANALYSIS_TYPES = ['Físico/Químico', 'Microbiológico'];

const QualityCriteriaPage = () => {
    const can = usePagePermissions('criterio_de_qualidade');

    // Estados Locais
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Estados do Formulário
    const [analysisInput, setAnalysisInput] = useState('');
    const [methodInput, setMethodInput] = useState('');
    const [specificationInput, setSpecificationInput] = useState('');
    const [analysisTypeInput, setAnalysisTypeInput] = useState(ANALYSIS_TYPES[0]);
    const [methodDescriptionInput, setMethodDescriptionInput] = useState('');
    const [revisionReason, setRevisionReason] = useState('');

    // Hook de Dados
    const {
        data: qualityCriteria,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useFirestore(QUALITY_CRITERIA_COLLECTION, 'analysis');

    // ============================================================
    // 1. DEFINIR filteredCriteria ANTES dos callbacks que o usam
    // ============================================================
    const filteredCriteria = useMemo(() => {
        if (!qualityCriteria) return [];
        if (!searchTerm) return qualityCriteria;

        const lowerCaseSearch = searchTerm.toLowerCase();
        return qualityCriteria.filter(item =>
            (item.analysis && item.analysis.toLowerCase().includes(lowerCaseSearch)) ||
            (item.method && item.method.toLowerCase().includes(lowerCaseSearch)) ||
            (item.specification && item.specification.toLowerCase().includes(lowerCaseSearch)) ||
            (item.analysisType && item.analysisType.toLowerCase().includes(lowerCaseSearch))
        );
    }, [qualityCriteria, searchTerm]);

    const totalFilteredItems = filteredCriteria.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentCriteria = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredCriteria.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredCriteria, currentPage]);

    // Reset do formulário
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setAnalysisInput('');
        setMethodInput('');
        setSpecificationInput('');
        setAnalysisTypeInput(ANALYSIS_TYPES[0]);
        setMethodDescriptionInput('');
        setRevisionReason('');
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setAnalysisInput(item.analysis || '');
        setMethodInput(item.method || '');
        setSpecificationInput(item.specification || '');
        setAnalysisTypeInput(item.analysisType || ANALYSIS_TYPES[0]);
        setMethodDescriptionInput(item.methodDescription || '');
        setRevisionReason('');
        setFormError('');
        setFormSuccess('');
        setIsModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    // Confirmar Delete
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess('Critério deletado com sucesso!');
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                const remainingCount = filteredCriteria.length - 1;
                if (currentPage > 1 && remainingCount <= (currentPage - 1) * ITEMS_PER_PAGE) {
                    setCurrentPage(prev => prev - 1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError("Erro ao deletar critério: " + result.error);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar critério. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, deleteDocument, filteredCriteria, currentPage]);

    // Validação técnica dos campos
    const validateForm = useCallback(() => {
        if (!analysisInput || !analysisInput.trim()) {
            setFormError("O campo Análise (Parâmetro) é obrigatório.");
            return false;
        }

        if (!methodInput || !methodInput.trim()) {
            setFormError("O campo Método/Referência é obrigatório.");
            return false;
        }

        if (!specificationInput || !specificationInput.trim()) {
            setFormError("O campo Especificação (Resultado Aceitável) é obrigatório.");
            return false;
        }

        if (analysisTypeInput === 'Microbiológico') {
            const lowerSpec = specificationInput.toLowerCase();
            if (!lowerSpec.includes('ausência') && !lowerSpec.includes('ausencia') &&
                !lowerSpec.includes('max') && !lowerSpec.includes('máximo') &&
                !lowerSpec.includes('maximo') && !lowerSpec.includes('<')) {
                setFormError("Para análises microbiológicas, informe um padrão como 'Ausência em 25g' ou 'Máx. 100 UFC/g'.");
                return false;
            }
        }

        if (analysisTypeInput === 'Físico/Químico') {
            const hasRange = specificationInput.includes('-') ||
                specificationInput.toLowerCase().includes('max') ||
                specificationInput.toLowerCase().includes('min') ||
                specificationInput.includes('<') ||
                specificationInput.includes('>');

            if (!hasRange && !specificationInput.match(/^[\d.,]+$/)) {
                setFormError("Para análises físico/químicas, informe um intervalo (Ex: 6.0-7.5) ou valor máximo/mínimo.");
                return false;
            }
        }

        return true;
    }, [analysisInput, methodInput, specificationInput, analysisTypeInput]);

    // Submissão do formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!validateForm()) return;

        const userEmail = localStorage.getItem('userEmail') || 'Sistema';
        const timestamps = getTimestamps(editingItem, userEmail);

        let data = {
            analysis: analysisInput.trim(),
            method: methodInput.trim(),
            specification: specificationInput.trim(),
            analysisType: analysisTypeInput,
            methodDescription: methodDescriptionInput.trim(),
            ...timestamps,
        };

        setFormLoading(true);

        try {
            let result;

            if (editingItem) {
                const currentVersion = editingItem.version || 1;
                const revisionHistory = editingItem.revisionHistory || [];

                const hasSignificantChange =
                    editingItem.specification !== data.specification ||
                    editingItem.method !== data.method ||
                    editingItem.analysis !== data.analysis;

                if (hasSignificantChange) {
                    const revisionEntry = {
                        version: currentVersion,
                        date: editingItem.updatedAt || editingItem.createdAt,
                        by: editingItem.updatedBy || editingItem.createdBy || 'Desconhecido',
                        changes: revisionReason || 'Alteração técnica',
                        specification: editingItem.specification,
                        method: editingItem.method
                    };

                    data = {
                        ...data,
                        version: currentVersion + 1,
                        revisionHistory: [...revisionHistory, revisionEntry],
                        previousSpecification: editingItem.specification,
                        revisionReason: revisionReason || 'Atualização do critério'
                    };
                } else {
                    data = {
                        ...data,
                        version: editingItem.version || 1,
                        revisionHistory: editingItem.revisionHistory || []
                    };
                }

                result = await updateDocument(editingItem.id, data);
            } else {
                const initialData = {
                    ...data,
                    version: 1,
                    revisionHistory: []
                };
                result = await addDocument(initialData);
            }

            if (result.success) {
                setFormSuccess(editingItem ? 'Critério editado com sucesso!' : 'Critério adicionado com sucesso!');
                setTimeout(() => {
                    handleCloseModal();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingItem ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [analysisInput, methodInput, specificationInput, analysisTypeInput, methodDescriptionInput, revisionReason, editingItem, validateForm, addDocument, updateDocument, handleCloseModal]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    // Função para obter classe CSS baseada no tipo de análise
    const getAnalysisTypeClass = useCallback((type) => {
        if (type === 'Físico/Químico') return styles.typePhysicoChemical;
        if (type === 'Microbiológico') return styles.typeMicrobiological;
        return '';
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingItem ? `Editar: ${editingItem.analysis}` : 'Adicionar Novo Critério de Qualidade'}
                subtitle={editingItem && editingItem.version > 1 ? `Versão ${editingItem.version} - Alterado em: ${new Date(editingItem.updatedAt).toLocaleDateString()}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? '💾 Salvar Alterações' : '✨ Adicionar Critério'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="analysisType">
                        Tipo de Análise <span className={styles.required}>*</span>
                    </label>
                    <select
                        id="analysisType"
                        value={analysisTypeInput}
                        onChange={(e) => setAnalysisTypeInput(e.target.value)}
                        className={styles.select}
                        disabled={formLoading}
                        required
                    >
                        {ANALYSIS_TYPES.map(type => (
                            <option key={type} value={type}>{type}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formRow}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="analysisInput">
                            Análise (Parâmetro) <span className={styles.required}>*</span>
                        </label>
                        <input
                            id="analysisInput"
                            type="text"
                            value={analysisInput}
                            onChange={(e) => setAnalysisInput(e.target.value)}
                            placeholder="Ex: pH, Umidade, Contagem Bacteriana"
                            required
                            className={styles.input}
                            disabled={formLoading}
                            autoFocus
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label htmlFor="methodInput">
                            Método/Referência <span className={styles.required}>*</span>
                        </label>
                        <input
                            id="methodInput"
                            type="text"
                            value={methodInput}
                            onChange={(e) => setMethodInput(e.target.value)}
                            placeholder="Ex: APHA 9215B, NBR 10987"
                            required
                            className={styles.input}
                            disabled={formLoading}
                        />
                    </div>
                </div>

                <div className={styles.inputGroup}>
                    <label htmlFor="specificationInput">
                        Especificação (Resultado Aceitável) <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="specificationInput"
                        type="text"
                        value={specificationInput}
                        onChange={(e) => setSpecificationInput(e.target.value)}
                        placeholder={analysisTypeInput === 'Microbiológico'
                            ? "Ex: Ausência em 25g, Máx. 100 UFC/g"
                            : "Ex: 6.0 - 7.5, Máx. 14%"}
                        required
                        className={styles.input}
                        disabled={formLoading}
                    />
                    <small className={styles.fieldHint}>
                        {analysisTypeInput === 'Microbiológico'
                            ? "Utilize 'Ausência', 'Máx.' ou '<' para limites microbiológicos"
                            : "Utilize intervalos (6.0-7.5) ou valores máximos/mínimos (Máx. 50)"}
                    </small>
                </div>

                <div className={styles.inputGroup}>
                    <label htmlFor="methodDescriptionInput">Descrição Detalhada do Método (Opcional)</label>
                    <textarea
                        id="methodDescriptionInput"
                        value={methodDescriptionInput}
                        onChange={(e) => setMethodDescriptionInput(e.target.value)}
                        placeholder="Descreva aqui o procedimento completo ou observações importantes sobre o método de análise."
                        className={styles.textarea}
                        disabled={formLoading}
                        rows="4"
                    />
                </div>

                {editingItem && (
                    <div className={styles.inputGroup}>
                        <label htmlFor="revisionReason">Motivo da Revisão</label>
                        <input
                            id="revisionReason"
                            type="text"
                            value={revisionReason}
                            onChange={(e) => setRevisionReason(e.target.value)}
                            placeholder="Ex: Atualização conforme nova norma, Correção de limite..."
                            className={styles.input}
                            disabled={formLoading}
                        />
                        <small className={styles.fieldHint}>Informe o motivo da alteração para auditoria</small>
                    </div>
                )}
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja deletar o critério "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita e pode afetar a integridade dos dados.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header */}
            <PageHeader
                title="Critérios de Qualidade"
                actionText="Novo Critério"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar por Análise, Método, Especificação ou Tipo..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar lista: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={5} rows={ITEMS_PER_PAGE} />
                    ) : currentCriteria.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '📋'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhum critério de qualidade cadastrado'
                            }
                            message={
                                searchTerm
                                    ? 'Tente outros termos de busca'
                                    : "Clique em 'Novo Critério' para começar"
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Tipo</th>
                                            <th>Análise (Parâmetro)</th>
                                            <th>Método/Referência</th>
                                            <th>Especificação</th>
                                            <th style={{ width: '140px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentCriteria.map((item) => (
                                            <tr key={item.id} className={styles.tableRow}>
                                                <td className={getAnalysisTypeClass(item.analysisType)}>
                                                    {item.analysisType}
                                                </td>
                                                <td>
                                                    {item.analysis}
                                                    {item.version > 1 && (
                                                        <span className={styles.versionIndicator}>
                                                            v{item.version}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{item.method}</td>
                                                <td>{item.specification}</td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(item.id, item.analysis)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading}
                                                        >
                                                            Deletar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="critérios"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default QualityCriteriaPage;